import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../api/client';

const ThemeContext = createContext();

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

function applyTheme(theme) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem('theme');
    const initial = (stored === 'dark' || stored === 'light') ? stored : 'light';
    applyTheme(initial);
    return initial;
  });

  // Sync theme from server profile when the app loads (enables cross-browser sync)
  // Only runs when a token exists — skip on the login page to avoid 401 redirect loops
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    apiClient.get('/auth/me').then(res => {
      const serverTheme = res?.data?.user?.theme;
      if (serverTheme === 'dark' || serverTheme === 'light') {
        setThemeState(serverTheme);
        localStorage.setItem('theme', serverTheme);
        applyTheme(serverTheme);
      }
    }).catch(() => { /* token expired or network error — keep local preference */ });
  }, []);

  const setTheme = (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') return;
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    // Fire-and-forget — persist to DB in background
    apiClient.patch('/users/me/theme', { theme: newTheme }).catch(() => {});
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
